<script setup lang="ts">
import { Permission, ScriptGetAllParams, ScriptModel } from '@/types';
import { CodeIcon } from '@heroicons/vue/solid';
import { onMounted, ref } from 'vue';
import ScriptListItem from './components/ScriptListItem.vue';
import { useFilterStore, useUiStore } from '@/stores';
import { toast, toastServiceError } from '@/services';
import ScriptService from '@/services/api/ScriptService';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();
const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const scripts = ref<ScriptModel[]>([]);

const fetchAllScripts = async () => {
    try {
        setLoading(true);
        const { data } = await ScriptService.getAll<ScriptModel[]>();
        scripts.value = data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const updateScriptStatus = async (script: ScriptModel, isActive: boolean) => {
    try {
        setLoading(true);
        const { data } = await (isActive
            ? ScriptService.activate(script.id)
            : ScriptService.inactivate(script.id));
        toast.success(data.message);
        await fetchAllScripts();
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const handleActivateClick = (script: ScriptModel) => updateScriptStatus(script, true);
const handleInactivateClick = (script: ScriptModel) => updateScriptStatus(script, false);

onMounted(() => {
    if (route.query.clearStateOnSetup) {
        router.replace({ query: {} });
    }

    fetchAllScripts();
});
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="flex items-center text-xl font-semibold text-gray-800">
                    <CodeIcon class="mr-1 h-6 w-6 text-gray-500" aria-hidden="true" />
                    {{ $t('core.entity.Scripts') }}
                </h1>
            </template>
            <template #actions>
                <router-link v-if="$can(Permission.CREATE_SCRIPT)" :to="{ name: 'CreateScript' }">
                    <UiButton variant="primary" text-variant="white">
                        {{ $t('core.actions.Add') }}
                    </UiButton>
                </router-link>
            </template>
        </UiPageHeader>

        <div class="space-y-2">
            <div v-if="scripts.length" class="space-y-2">
                <ScriptListItem
                    v-for="script in scripts"
                    :key="script.id"
                    :model-value="script"
                    @activate="handleActivateClick"
                    @inactivate="handleInactivateClick"
                />
            </div>
            <UiPanel v-else class="flex items-center justify-center py-8 text-gray-500">
                {{ $t('core.messages.noRecordsFound') }}
            </UiPanel>
        </div>
    </div>
</template>
